import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { emptyDivTestId } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { getTagsMap, TagsInfoType } from "../../helpers/resourceHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_DB_SYSTEM_UPDATE_POLL_DELAY,
  VMDB_DB_SYSTEM_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationConfirmationProps } from "../OperationTypes";

export interface VmDbSystemUpdateTagsProps extends OperationConfirmationProps {
  dbSystemId: string | undefined;
  tags: TagsInfoType;
}

const VmDbSystemUpdateTags = (
  { location, dbSystemId, tags, onExecute }: VmDbSystemUpdateTagsProps,
): JSX.Element => {
  const { invokeCall: invokeTagUpdateSystem } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.updateVmdbDbSystem },
  );

  React.useEffect(() => {
    const { subscriptionId, resourceGroup, resourceName } = parseId(dbSystemId);
    const resGroup = decodeURIComponent(resourceGroup || "");
    const resName = decodeURIComponent(resourceName || "");

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: onExecute,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.applyTags(),
          message: Messages.notifications.inProgress.messages.applyTags(resName),
        },
        success: {
          title: Messages.notifications.success.titles.applyTags(),
          message: Messages.notifications.success.messages.applyTags(resName),
        },
        failure: {
          title: Messages.notifications.failure.titles.applyTags(),
          message: Messages.notifications.failure.messages.applyTags(resName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.VMDB_DB_SYSTEM_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName: resourceGroup,
            dbSystemName: resName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.VMDB_DB_SYSTEM_UPDATED_CHECK,
          delay: VMDB_DB_SYSTEM_UPDATE_POLL_DELAY,
          interval: VMDB_DB_SYSTEM_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeTagUpdateSystem({
      subscriptionId,
      resourceGroupName: resGroup,
      dbSystemName: resName,
      updateDbSystemDetails: { freeformTags: getTagsMap(tags) },
    }, invokeOptions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div data-test-id={emptyDivTestId} />;
};

export const newVmDbSystemUpdateTags = (
  props: VmDbSystemUpdateTagsProps,
): JSX.Element => (<VmDbSystemUpdateTags {...props} />);
